import React from 'react';

import { ProfitLoss } from '../../ProfitLoss';
import { StrategyNFTCollection } from './types';
import { useTranslation } from 'react-i18next';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { Icon } from '../../UI';
import { calculateRoi } from './helper';

interface NFTCollectionDetailsProps {
  strategyNftCollection: StrategyNFTCollection;
  goBack: () => void;
  width: number;
}

const NFTCollectionDetails: React.FC<NFTCollectionDetailsProps> = ({
  strategyNftCollection,
  goBack,
  width,
}) => {
  const { t } = useTranslation();

  const { imageUrl, name } = strategyNftCollection.collection;

  const collectionValues = calculateRoi(strategyNftCollection);

  return (
    <div className="h-full flex flex-col">
      {/* HEADER */}
      <div className="flex items-center border-b border-grey-10 py-2 px-4 gap-3 dark:border-grey-70">
        <Icon svgName="arrowBack" className="cursor-pointer" onClick={goBack} />
        <img
          src={imageUrl}
          alt="collection avatar"
          className="w-12 h-12 object-cover rounded-full"
        />
        <p className="font-semibold text-base -mx-1">{name}</p>
      </div>

      {/* STATS */}
      <div
        className="overflow-y-auto pb-4 "
        id={'NFT-scrollableDiv'}
        style={{ contentVisibility: 'auto' }}
      >
        {/* STATISTICS */}
        <div className="w-full mt-4">
          <div className="grid grid-cols-3 px-4 gap-4">
            <div className="bg-grey-5 dark:bg-grey-80 rounded-xl px-5 py-3">
              <p className="text-grey-70 dark:text-grey-30 text-xs mb-1">
                {t('widget.nft.collectionView.collectionItems')}
              </p>
              <p className="text-md">{strategyNftCollection.tokenCount}</p>
            </div>
            <div className="bg-grey-5 dark:bg-grey-80 rounded-xl px-5 py-3">
              <p className="text-grey-70 dark:text-grey-30 text-xs mb-1">
                {t('widget.nft.collectionView.totalValue')}
              </p>
              <p className="text-md">
                <CustomNumber value={collectionValues.usd || 0} />
              </p>
            </div>
            <div className="bg-grey-5 dark:bg-grey-80 rounded-xl px-5 py-3">
              <p className="text-grey-70 dark:text-grey-30 text-xs mb-1">
                {t('widget.nft.collectionView.totalROI')}
              </p>
              <div className="flex items-center gap-2">
                <p className="text-md">
                  <CustomNumber
                    value={collectionValues.roi || 0}
                    displayType={'text'}
                  />
                </p>
                <ProfitLoss
                  percentage={collectionValues.roiPercentage}
                  displayType={'text'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* NFTs */}
        <div className={`grid grid-cols-${width} px-4 mt-4 gap-4`}>
          {strategyNftCollection.tokens.items.map((nftAsset) => {
            const roi =
              nftAsset.token.floorAskPriceUsd -
              nftAsset.strategyAsset.entryPrice;
            const roiPercentage =
              ((nftAsset.token.floorAskPriceUsd -
                nftAsset.strategyAsset.entryPrice) /
                nftAsset.strategyAsset.entryPrice) *
              100;

            return (
              <div
                key={nftAsset.token.id}
                className="flex rounded-xl border border-grey-10 dark:border-grey-70 shadow-light "
                // onClick={() => setSelectedToken(nftAsset)}
              >
                <div className="w-1/2">
                  <img
                    src={nftAsset.token.imageUrl}
                    alt="token"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
                <div className="ml-3 py-2 w-1/2 flex flex-col gap-2 text-sm">
                  <div>
                    <p className="text-md">{nftAsset.token.name}</p>
                    <p className="font-bold text-md">
                      #{nftAsset.token.tokenId}
                    </p>
                  </div>
                  <div>
                    <p className="text-grey-70 text-xs">
                      {t('widget.nft.collectionView.card.floorPrice')}
                    </p>
                    <div className="flex gap-1">
                      <p className={'text-base'}>
                        <CustomNumber
                          value={nftAsset.token.floorAskPriceUsd ?? 0}
                          displayType={'text'}
                        />
                      </p>
                      <img src="/icons/eth.svg" alt="eth" />
                      <CustomNumber
                        value={nftAsset.token.floorAskPrice ?? 0}
                        displayType={'text'}
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-grey-70 text-xs">
                      {t('widget.nft.collectionView.card.purchasePrice')}
                    </p>
                    <p className={'text-base'}>
                      <CustomNumber
                        value={nftAsset.token.floorAskPrice ?? 0}
                        displayType={'text'}
                      />
                    </p>
                  </div>
                  <div>
                    <p className="text-grey-70 text-xs">
                      {t('widget.nft.collectionView.card.ROI')}
                    </p>
                    <div className="flex gap-1">
                      <p className={'text-base'}>
                        <CustomNumber value={roi ?? 0} displayType={'text'} />
                      </p>
                      <ProfitLoss percentage={roiPercentage} size={'small'} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NFTCollectionDetails;
